import React from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useCache } from '../../contexts/CacheContext';
import { Subscription, SubscriptionPlan } from '../../types/subscription';
import { LocalUsageStats } from '../../services/localUsageService';
import { getPlanById } from '../../constants/subscriptionPlans';
import { SubscriptionUtils } from '../../utils/subscriptionUtils';
import { usePlanLimitations } from '../../hooks/usePlanLimitations';
import PlanLimitationWarning from './PlanLimitationWarning';

interface UsageDashboardProps {
  subscription: Subscription;
  className?: string;
}

interface UsageBarProps {
  label: string;
  current: number;
  limit: number;
  unit: string;
  isUnlimited?: boolean;
  warningThreshold?: number;
  criticalThreshold?: number;
}

const UsageBar: React.FC<UsageBarProps> = ({
  label,
  current,
  limit,
  unit,
  isUnlimited = false,
  warningThreshold = 80,
  criticalThreshold = 95
}) => {
  const { language, strings } = useLocalization();
  const percentage = isUnlimited ? 0 : (current / limit) * 100;
  const isWarning = percentage >= warningThreshold && percentage < criticalThreshold;
  const isCritical = percentage >= criticalThreshold;
  
  const getBarColor = () => {
    if (isUnlimited) return 'bg-gray-200';
    if (isCritical) return 'bg-red-500';
    if (isWarning) return 'bg-yellow-500';
    return 'bg-primary';
  };

  const getTextColor = () => {
    if (isUnlimited) return 'text-gray-600';
    if (isCritical) return 'text-red-600';
    if (isWarning) return 'text-yellow-600';
    return 'text-blue-600';
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className={`text-sm font-semibold ${getTextColor()}`}>
          {isUnlimited ? `${current} ${unit} (${strings.subscriptions.unlimited})` : `${current} / ${limit} ${unit}`}
        </span>
      </div>
      
      {!isUnlimited && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${getBarColor()}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      )}
      
      {isUnlimited && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="h-2 rounded-full bg-gradient-to-r from-green-400 to-green-600" 
            style={{ width: current > 0 ? '100%' : '0%' }}
          />
        </div>
      )}
      
      {!isUnlimited && (isWarning || isCritical) && (
        <p className={`text-xs ${isCritical ? 'text-red-600' : 'text-yellow-600'}`}>
          {isCritical 
            ? (language === 'ar' ? '⚠️ تم تجاوز الحد! فكر في ترقية خطتك.' : '⚠️ Limit exceeded! Consider upgrading your plan.')
            : (language === 'ar' ? '⚠️ تقترب من الحد. فكر في الترقية قريباً.' : '⚠️ Approaching limit. Consider upgrading soon.')
          }
        </p>
      )}
    </div>
  );
};

const UsageDashboard: React.FC<UsageDashboardProps> = ({
  subscription,
  className = ''
}) => {
  const { language, strings } = useLocalization();
  const { getUsageStats } = useCache();
  const { limitations, shouldShowUpgradePrompt } = usePlanLimitations();
  const plan = getPlanById(subscription.planId);
  
  // Get usage stats from cache
  const usageStats = getUsageStats();
  
  if (!plan) {
    return (
      <div className={`p-6 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <p className="text-red-600">{language === 'ar' ? 'خطأ: لم يتم العثور على خطة الاشتراك' : 'Error: Subscription plan not found'}</p>
      </div>
    );
  }

  if (!usageStats) {
    return (
      <div className={`p-6 bg-gray-50 border border-gray-200 rounded-lg ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded"></div>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          {language === 'ar' ? 'جاري تحميل إحصائيات الاستخدام...' : 'Loading usage statistics...'}
        </p>
      </div>
    );
  }

  const isInTrial = SubscriptionUtils.isInTrialPeriod(subscription);
  const trialDaysRemaining = SubscriptionUtils.getTrialDaysRemaining(subscription);

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{strings.subscriptions.usageDashboard}</h3>
            <p className="text-sm text-gray-600">
              {language === 'ar' ? 'الخطة الحالية:' : 'Current plan:'} <span className="font-medium">{language === 'ar' ? plan.nameAr : plan.name}</span>
            </p>
          </div>
          
          {isInTrial && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <p className="text-sm font-medium text-blue-800">
                🎉 {strings.subscriptions.freeTrial}
              </p>
              <p className="text-xs text-blue-600">
                {trialDaysRemaining} {strings.subscriptions.daysLeft}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Usage Stats */}
      <div className="p-6 space-y-6">
        {/* Offices Usage */}
        <UsageBar
          label={strings.subscriptions.offices}
          current={usageStats.officesCount}
          limit={plan.maxOffices}
          unit={language === 'ar' ? 'مكتب' : 'offices'}
          isUnlimited={plan.maxOffices === -1}
        />

        {/* Total Clients Usage */}
        <UsageBar
          label={language === 'ar' ? `إجمالي ${strings.subscriptions.clients}` : `Total ${strings.subscriptions.clients}`}
          current={usageStats.clientsCount}
          limit={plan.maxClientsTotal}
          unit={language === 'ar' ? 'عميل' : 'clients'}
          isUnlimited={plan.maxClientsTotal === -1}
        />

        {/* Total Files Usage */}
        <UsageBar
          label={language === 'ar' ? `إجمالي ${strings.subscriptions.files}` : `Total ${strings.subscriptions.files}`}
          current={usageStats.filesCount}
          limit={plan.maxFilesTotal}
          unit={language === 'ar' ? 'ملف' : 'files'}
          isUnlimited={plan.maxFilesTotal === -1}
        />

        {/* Plan Limitation Warnings */}
        {limitations && shouldShowUpgradePrompt() && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-900">
              {language === 'ar' ? 'تحذيرات الخطة' : 'Plan Warnings'}
            </h4>
            
            {limitations.offices.isExceeded && (
              <PlanLimitationWarning itemType="office" />
            )}
            
            {limitations.clients.isExceeded && (
              <PlanLimitationWarning itemType="client" />
            )}
            
            {limitations.files.isExceeded && (
              <PlanLimitationWarning itemType="file" />
            )}
            
            {limitations.teamMembers.isExceeded && (
              <PlanLimitationWarning itemType="team_member" />
            )}
          </div>
        )}

        {/* Last Updated */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center text-xs text-gray-500">
            <span>{language === 'ar' ? 'آخر تحديث:' : 'Last updated:'}</span>
            <span>{new Date(usageStats.lastUpdated).toLocaleString()}</span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default UsageDashboard;
